'use client';
import { useRef } from 'react';
import {
  animate,
  stagger,
  svg,
  useAnime,
  DURATION,
  EASE,
} from '@/lib/anime';

export function Hero() {
  const rootRef = useRef<HTMLElement>(null);

  // Soft entrance: title + tagline + CTAs fade up gently. No
  // theatrical word-by-word deploy - restraint reads as confidence,
  // not flash. Tiny y offset (12 px) keeps the gradient spans inside
  // their layout so `bg-clip-text` never breaks. Reduced-motion:
  // duration 0 - instant final state.
  useAnime<HTMLElement>(rootRef, (self) => {
    const { reduceMotion } = self.matches;

    // Logo signature: the three orbital ellipses line-draw on, the
    // six nodes + core pop in staggered, then the whole mark settles
    // into a slow continuous breathing glow. Above-the-fold, so the
    // SVG ships fully drawn in the static HTML (the `draw` tween only
    // re-runs the stroke when motion is allowed). Reduced-motion users
    // see the static mark with no draw, no pop, no breathing.
    if (!reduceMotion) {
      const orbits = svg.createDrawable('[data-logo-orbit]');
      animate(orbits, {
        draw: ['0 0', '0 1'],
        delay: stagger(140),
        duration: DURATION.long,
        ease: 'inOutSine',
      });
      animate('[data-logo-node]', {
        scale: [0, 1],
        opacity: [0, 1],
        delay: stagger(50, { start: 380, from: 'center' }),
        duration: DURATION.short,
        ease: EASE.spring,
      });
      // Ambient breathing — restrained scale pulse on the whole mark,
      // bounded + alternating so RAF parks when the tab is hidden via
      // anime's engine. Starts after the entrance has resolved.
      animate('[data-logo-mark]', {
        scale: [1, 1.035, 1],
        delay: DURATION.long + 200,
        duration: DURATION.ambient,
        loop: true,
        alternate: true,
        ease: 'inOutSine',
      });
    }

    animate('h1[data-hero-title]', {
      y: reduceMotion ? 0 : [12, 0],
      opacity: [0, 1],
      duration: reduceMotion ? 0 : DURATION.medium,
      ease: EASE.authoritative,
    });
    animate('p[data-hero-tagline]', {
      y: reduceMotion ? 0 : [12, 0],
      opacity: [0, 1],
      delay: reduceMotion ? 0 : 200,
      duration: reduceMotion ? 0 : DURATION.short,
      ease: EASE.authoritative,
    });
    animate('[data-hero-cta]', {
      y: reduceMotion ? 0 : [8, 0],
      opacity: [0, 1],
      delay: stagger(reduceMotion ? 0 : 60, { start: reduceMotion ? 0 : 400 }),
      duration: reduceMotion ? 0 : DURATION.short,
      ease: EASE.authoritative,
    });
  });

  return (
    // HERO - no <Reveal> wrappers: above-the-fold content must render
    // at full opacity in SSR HTML so Lighthouse can detect FCP. The
    // `Reveal` component starts at opacity-0, which left the entire
    // first viewport invisible until IntersectionObserver fired post-
    // hydration; Chrome's paint heuristic missed FCP entirely (FCP
    // requires text/image, not the SpaceBackground canvas).
    <section ref={rootRef} className="min-h-screen flex flex-col items-center justify-center px-6 text-center pt-20">
      <div className="mb-8">
        {/* Inline orbital "atom" mark, identical geometry to
            public/synapseia-logo.svg. Inlined (vs <Image>) so anime.js
            can line-draw the orbits + pop the nodes on mount. Same
            96/112 px box + drop-shadow as the prior PNG, so layout is
            unchanged (no CLS). The full mark is present in the static
            HTML, so reduced-motion / no-JS visitors see the complete
            logo with no draw. `data-logo-mark` is the breathing target;
            `data-logo-orbit` the drawables; `data-logo-node` the pops. */}
        <svg
          data-logo-mark
          viewBox="0 0 100 100"
          role="img"
          aria-label="Synapseia Network"
          className="w-24 h-24 sm:w-28 sm:h-28 mx-auto drop-shadow-[0_0_40px_rgba(100,120,255,0.15)] will-change-transform"
        >
          <defs>
            <linearGradient id="hero-oc-grad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#22d3ee" />
              <stop offset="50%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#a78bfa" />
            </linearGradient>
          </defs>
          <g fill="none" stroke="url(#hero-oc-grad)" strokeWidth={1.5}>
            <ellipse data-logo-orbit cx={50} cy={50} rx={36} ry={14} opacity={0.8} />
            <ellipse data-logo-orbit cx={50} cy={50} rx={36} ry={14} opacity={0.6} transform="rotate(60 50 50)" />
            <ellipse data-logo-orbit cx={50} cy={50} rx={36} ry={14} opacity={0.6} transform="rotate(-60 50 50)" />
          </g>
          <circle data-logo-node cx={50} cy={50} r={7} fill="url(#hero-oc-grad)" style={{ transformBox: 'fill-box', transformOrigin: 'center' }} />
          <g fill="url(#hero-oc-grad)">
            <circle data-logo-node cx={86} cy={50} r={3} style={{ transformBox: 'fill-box', transformOrigin: 'center' }} />
            <circle data-logo-node cx={14} cy={50} r={3} style={{ transformBox: 'fill-box', transformOrigin: 'center' }} />
            <circle data-logo-node cx={68} cy={20} r={3} style={{ transformBox: 'fill-box', transformOrigin: 'center' }} />
            <circle data-logo-node cx={32} cy={80} r={3} style={{ transformBox: 'fill-box', transformOrigin: 'center' }} />
            <circle data-logo-node cx={32} cy={20} r={3} style={{ transformBox: 'fill-box', transformOrigin: 'center' }} />
            <circle data-logo-node cx={68} cy={80} r={3} style={{ transformBox: 'fill-box', transformOrigin: 'center' }} />
          </g>
        </svg>
      </div>
      <h1 data-hero-title className="text-5xl sm:text-7xl font-bold tracking-tight mb-6 leading-[1.05]">
        <span data-word className="inline-block bg-gradient-to-b from-white via-slate-200 to-slate-400 bg-clip-text text-transparent will-change-transform">Synapseia</span><br />
        <span data-word className="inline-block bg-gradient-to-r from-slate-300 via-blue-200 to-slate-300 bg-clip-text text-transparent will-change-transform">Network</span>
      </h1>
      <p data-hero-tagline className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-12 leading-relaxed">
        A distributed P2P network of independent AI agents that run multiple
        research training tracks in parallel, analyzing literature,
        peer-reviewing each other&apos;s outputs, and consolidating findings
        into a shared knowledge graph that every node can query.
      </p>
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <button data-hero-cta onClick={() => document.getElementById('engine')?.scrollIntoView({ behavior: 'smooth' })} className="px-8 py-3.5 rounded-xl backdrop-blur-md bg-white/[0.04] border border-white/[0.08] text-slate-200 font-medium text-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300">
          How it works
        </button>
      </div>
    </section>
  );
}
